import { FC, useEffect } from 'react';
import { Form, Input, Button, AutoComplete } from 'antd';
import { CommunicationTagKey, EmailCommunicationRecord } from '#types';
import { MAX_LENGTHS } from '#types/System.Parameters.types';
import FlexCol from '../../../../atoms/FlexCol';
import FlexBox from '../../../../atoms/FlexBox';
import RecipientSelect from '../../../../selectors/RecipientSelect';
import { useContactSearch } from '../../../../../use/useContactSearch';

export type EmailFormData = {
    tag: CommunicationTagKey;
    from?: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body?: string;
    contactEntityRecordId?: string;
}

interface EmailPopoverFormProps {
    initialValues?: EmailCommunicationRecord;
    onSave: (data: EmailFormData) => boolean | Promise<boolean>;
    onCancel: () => void;
    disabled?: boolean;
}

const EmailPopoverForm: FC<EmailPopoverFormProps> = ({
    initialValues,
    onSave,
    onCancel,
    disabled = false,
}) => {
    const [form] = Form.useForm<EmailFormData>();
    const { options: senderOptions, loading: senderLoading, search: searchSenders } = useContactSearch({
        kind: 'email',
    });

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                tag: initialValues.tag || 'manual',
                from: initialValues.from,
                to: initialValues.to,
                cc: initialValues.cc,
                bcc: initialValues.bcc,
                subject: initialValues.subject,
                body: initialValues.body,
                contactEntityRecordId: initialValues.contactEntityRecordId,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                tag: 'manual',
                to: [],
                cc: [],
                bcc: [],
            });
        }
    }, [form, initialValues]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const success = await onSave(values);
            if (success) {
                form.resetFields();
            }
        } catch (error) {
            console.error('[EmailPopoverForm] validation failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <FlexCol gap={5} style={{ width: 400, maxWidth: '90vw' }}>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    tag: 'manual',
                    to: [],
                    cc: [],
                    bcc: [],
                    contactEntityRecordId: undefined,
                }}
                disabled={disabled}
                style={{ width: '100%' }}
                variant='filled'
            >
                {/* Tag selection removed as per requirement: defaulting to 'manual' */}
                <Form.Item
                    name="tag"
                    hidden
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="contactEntityRecordId"
                    hidden
                >
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item
                    name="from"
                    label="From"
                    rules={[{ type: 'email', message: 'Invalid email' }]}
                >
                    <AutoComplete
                        options={senderOptions}
                        showSearch={{ filterOption: false, onSearch: searchSenders }}
                        optionRender={(option) => option.data.detail}
                        notFoundContent={senderLoading ? 'Searching contacts...' : 'Type a sender email'}
                        placeholder="Sender email"
                        aria-label="email-from-input"
                        allowClear
                        backfill={false}
                        disabled={disabled}
                        variant='filled'
                    />
                </Form.Item>
                <Form.Item
                    name="to"
                    label="To"
                >
                    <RecipientSelect
                        placeholder="Recipients"
                        ariaLabel="email-to-select"
                        disabled={disabled}
                        maxTagCount="responsive"
                    />
                </Form.Item>
                <Form.Item
                    name="cc"
                    label="CC"
                >
                    <RecipientSelect
                        placeholder="CC"
                        ariaLabel="email-cc-select"
                        disabled={disabled}
                        maxTagCount="responsive"
                    />
                </Form.Item>
                <Form.Item
                    name="bcc"
                    label="BCC"
                >
                    <RecipientSelect
                        placeholder="BCC"
                        ariaLabel="email-bcc-select"
                        disabled={disabled}
                        maxTagCount="responsive"
                    />
                </Form.Item>
                <Form.Item
                    name="subject"
                    label="Subject"
                >
                    <Input 
                        placeholder="Subject" 
                        aria-label="email-subject-input"
                        variant='filled'
                    />
                </Form.Item>
                <Form.Item
                    name="body"
                    label="Message"
                    rules={[
                        { max: MAX_LENGTHS.longText, message: `Message cannot exceed ${MAX_LENGTHS.longText} characters` }
                    ]}
                >
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        placeholder="Body"
                        aria-label="email-body-input"
                        style={{ marginBottom: 10 }}
                        maxLength={MAX_LENGTHS.longText}
                        showCount
                        variant='filled'
                    />
                </Form.Item>
            </Form>
            <FlexBox stretch>
                <Button 
                    onClick={handleCancel} 
                    aria-label="cancel-email-edit"
                >
                    Cancel
                </Button>
                <Button 
                    type="primary" 
                    onClick={handleSave} 
                    aria-label="save-email-edit"
                >
                    Save
                </Button>
            </FlexBox>
        </FlexCol>
    );
}
export default EmailPopoverForm;
